import React, { useState, useEffect } from 'react';
import { hasCourseAccess } from '../utils/emailEncoder';
import GlobalHeader from './GlobalHeader';

interface User {
  name: string;
  email: string;
  picture?: string;
}

interface LessonViewerProps {
  courseId: string;
  filename: string;
  user: User | null;
  onSignOut: () => void;
  onSignIn: () => void;
}

const LessonViewer: React.FC<LessonViewerProps> = ({
  courseId,
  filename,
  user,
  onSignOut,
  onSignIn
}) => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      try {
        const access = await hasCourseAccess(user.email, courseId);
        setHasAccess(access);
        if (!access) {
          setError('You do not have access to this course.');
        }
      } catch (err) {
        console.error('Error checking access:', err);
        setError('Error checking course access.');
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [user, courseId]);

  // Build the lesson URL - load from public folder
  const getLessonUrl = () => {
    return `/lessons/${filename}`;
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #4F46E5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>Loading lesson...</div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%)',
        padding: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '24px',
            background: '#F3F4F6',
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>🔒</div>
          <h2 style={{ margin: '0 0 12px 0', color: '#111827', fontSize: '24px', fontWeight: '700' }}>
            Sign In Required
          </h2>
          <p style={{ margin: '0 0 32px 0', color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>
            Please sign in to view this lesson content.
          </p>
          <button
            onClick={() => window.close()}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              width: '100%',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.1), 0 2px 4px -1px rgba(79, 70, 229, 0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(79, 70, 229, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(79, 70, 229, 0.1)';
            }}
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%)',
        padding: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '24px',
            background: '#FEF2F2',
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>🚫</div>
          <h2 style={{ margin: '0 0 12px 0', color: '#111827', fontSize: '24px', fontWeight: '700' }}>
            Access Denied
          </h2>
          <p style={{ margin: '0 0 32px 0', color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>
            {error || 'You do not have access to this course lesson.'}
          </p>
          <button
            onClick={() => window.close()}
            style={{
              padding: '12px 24px',
              backgroundColor: 'white',
              color: '#4B5563',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              width: '100%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%)'
    }}>
      <GlobalHeader user={user} onSignOut={onSignOut} onSignIn={onSignIn} />

      {/* Lesson Content */}
      <div style={{
        padding: '100px 24px 40px', // Added top padding for fixed header
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: 'calc(100vh - 80px)'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          height: 'calc(100vh - 140px)', // Full height minus padding
          minHeight: '600px'
        }}>
          <iframe
            src={getLessonUrl()}
            frameBorder="0"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white'
            }}
            title="Lesson Instructions"
          />
        </div>
      </div>
    </div>
  );
};

export default LessonViewer;

